import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../../hooks/useSeo';
import { useAuth } from '../../context/AuthContext';
import { useCateringData } from '../../hooks/useCateringData';
import QuoteForm from './QuoteForm';
import InlineItemEditor from '../../Components/AdminEdit/InlineItemEditor';
import {
  CategoryTitleBar, AddCategoryPanel, CategoryOrderPanel, countLabel,
} from '../../Components/AdminEdit/CategoryTools';
import { orderedCards, moveCard, updateCard } from './cateringHelpers';
import '../../Components/AdminEdit/AdminEditBar.css';
import './Catering.css';

const useReveal = () => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('ct-visible'); obs.unobserve(el); } },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const Reveal = ({ children, className = '' }) => {
  const ref = useReveal();
  return <div ref={ref} className={`ct-reveal ${className}`}>{children}</div>;
};

/* ── Categories built in the editor ──────────────────────────────────────── */
const CATERING_LAYOUTS = [
  {
    id: 'trays',
    label: 'Half / Full trays',
    hint: 'Grouped like Starters — coloured VEG / NON-VEG bands, half & full tray prices.',
  },
  {
    id: 'table',
    label: 'Simple table',
    hint: 'A flat list with two value columns, like Breads or Breakfast.',
    needsColumns: true,
    columnsPlaceholder: 'Two column labels, comma separated — e.g. Min., Price',
  },
];

/** Build an empty catering category in whichever shape the admin picked. */
const buildCateringSection = ({ title, icon, layout, columns }) => {
  const base = { id: `c${Date.now().toString(36)}`, title, icon: icon || '🍽️' };
  if (layout === 'table') {
    return { ...base, kind: 'table', cols: ['Item', columns[0] || 'Half', columns[1] || 'Full'], items: [] };
  }
  return { ...base, kind: 'trays', groups: [{ label: 'ITEMS', color: '#f59e0b', items: [] }] };
};

const TraySection = ({ data, defaultOpen = false, isEditing = false, onUpdate, category }) => {
  const [open, setOpen] = useState(defaultOpen);
  const groups = data.groups || [];
  const count = groups.reduce((s, g) => s + g.items.length, 0);

  const setGroups = (next) => onUpdate?.({ ...data, groups: next });

  /* ── Group (sub-category) create / rename / delete / reorder ── */
  // Kept verbatim — renaming doesn't reformat either, and the existing bands
  // are only all-caps because they were typed that way.
  const handleAddGroup = ({ title }) => setGroups([
    ...groups, { label: title, color: '#f59e0b', items: [] },
  ]);
  const handleRenameGroup = (gi, label, color) => setGroups(
    groups.map((g, i) => (i === gi ? { ...g, label, color: color || g.color } : g)),
  );
  const handleDeleteGroup = (gi) => setGroups(groups.filter((_, i) => i !== gi));
  const handleMoveGroup = (gi, dir) => {
    const next = [...groups];
    const to = gi + dir;
    if (to < 0 || to >= next.length) return;
    [next[gi], next[to]] = [next[to], next[gi]];
    setGroups(next);
  };

  const [newItemPos, setNewItemPos] = useState(null);
  const handleSaveItem = (gi, ii, updated) => {
    const newGroups = groups.map((g, i) =>
      i === gi ? { ...g, items: g.items.map((it, j) => (j === ii ? updated : it)) } : g
    );
    onUpdate?.({ ...data, groups: newGroups });
    setNewItemPos(null);
  };
  const handleDeleteItem = (gi, ii) => {
    const newGroups = groups.map((g, i) =>
      i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g
    );
    onUpdate?.({ ...data, groups: newGroups });
    setNewItemPos(null);
  };
  const handleMoveItem = (gi, ii, dir) => {
    const newGroups = groups.map((g, i) => {
      if (i !== gi) return g;
      const items = [...g.items];
      const to = ii + dir;
      if (to < 0 || to >= items.length) return g;
      [items[ii], items[to]] = [items[to], items[ii]];
      return { ...g, items };
    });
    onUpdate?.({ ...data, groups: newGroups });
  };
  const handleAddItem = (gi) => {
    const newGroups = groups.map((g, i) =>
      i === gi ? { ...g, items: [...g.items, ['', '', '']] } : g
    );
    const newLen = groups[gi].items.length;
    onUpdate?.({ ...data, groups: newGroups });
    setNewItemPos({ gi, ii: newLen });
  };

  return (
    <div className={`ct-card ${open ? 'ct-card--open' : ''}`}>
      <button className="ct-card__hd" onClick={() => setOpen(!open)}>
        <span className="ct-card__icon">{data.icon}</span>
        <span className="ct-card__title">{data.title}</span>
        <span className="ct-card__meta">{countLabel(count)}</span>
        <span className="ct-card__toggle">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="ct-card__body">
          {isEditing ? (
            <div className="ae-section">
              {category && (
                <CategoryTitleBar
                  title={data.title}
                  hint={countLabel(count)}
                  {...category}
                />
              )}
              {groups.map((g, gi) => (
                <div key={gi} className="ae-group">
                  <CategoryTitleBar
                    title={g.label}
                    label="Group"
                    color={g.color || '#52525b'}
                    hint={countLabel(g.items.length)}
                    takenNames={groups.map((x) => x.label)}
                    onRename={(label, color) => handleRenameGroup(gi, label, color)}
                    onDelete={() => handleDeleteGroup(gi)}
                    onMoveUp={gi > 0 ? () => handleMoveGroup(gi, -1) : undefined}
                    onMoveDown={gi < groups.length - 1 ? () => handleMoveGroup(gi, 1) : undefined}
                  />
                  {g.items.map((item, ii) => (
                    <InlineItemEditor
                      key={ii}
                      item={item}
                      mode="catering"
                      col2Label="Half"
                      col3Label="Full"
                      autoEdit={newItemPos?.gi === gi && newItemPos?.ii === ii}
                      onSave={(updated) => handleSaveItem(gi, ii, updated)}
                      onDelete={() => handleDeleteItem(gi, ii)}
                      onMoveUp={ii > 0 ? () => handleMoveItem(gi, ii, -1) : undefined}
                      onMoveDown={ii < g.items.length - 1 ? () => handleMoveItem(gi, ii, 1) : undefined}
                    />
                  ))}
                  <button className="ae-add-btn" onClick={() => handleAddItem(gi)}>+ Add Item</button>
                </div>
              ))}
              <AddCategoryPanel
                label="+ Add Group"
                heading={`New group in ${data.title}`}
                namePlaceholder="Group name — e.g. SEAFOOD"
                showIcon={false}
                takenNames={groups.map((g) => g.label)}
                onCreate={handleAddGroup}
              />
            </div>
          ) : (
            <>
              <div className="ct-card__colhd">
                <span>Item</span><span>Half Tray</span><span>Full Tray</span>
              </div>
              {groups.map((g, gi) => (
                <React.Fragment key={gi}>
                  <span className="ct-card__pill" style={{ background: g.color }}>{g.label}</span>
                  {g.items.map(([name, half, full], ii) => (
                    <div className="ct-card__row" key={ii}>
                      <span className="ct-card__item">{name}</span>
                      <span className="ct-card__price">{half === '—' ? '—' : `$${half}`}</span>
                      <span className="ct-card__price">${full}</span>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ItemTable = ({ items, cols, isEditing = false, onUpdate }) => {
  const [newIdx, setNewIdx] = useState(null);
  const handleSave   = (i, updated) => { onUpdate?.(items.map((it, j) => (j === i ? updated : it))); setNewIdx(null); };
  const handleDelete = (i)          => { onUpdate?.(items.filter((_, j) => j !== i)); setNewIdx(null); };
  const handleMove   = (i, dir)     => {
    const arr = [...items];
    const to = i + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[i], arr[to]] = [arr[to], arr[i]];
    onUpdate?.(arr);
  };
  const handleAdd    = ()           => { onUpdate?.([...items, ['', '', '']]); setNewIdx(items.length); };

  return (
    <div className="ct-table">
      {isEditing ? (
        <div className="ae-section" style={{ paddingTop: 0 }}>
          {items.map((item, i) => (
            <InlineItemEditor
              key={i}
              item={item}
              mode="table"
              col2Label={cols[1]}
              col3Label={cols[2]}
              autoEdit={newIdx === i}
              onSave={(updated) => handleSave(i, updated)}
              onDelete={() => handleDelete(i)}
              onMoveUp={i > 0 ? () => handleMove(i, -1) : undefined}
              onMoveDown={i < items.length - 1 ? () => handleMove(i, 1) : undefined}
            />
          ))}
          <button className="ae-add-btn" onClick={handleAdd}>+ Add Item</button>
        </div>
      ) : (
        <>
          <div className="ct-table__hd">
            {cols.map((c, i) => <span key={i}>{c}</span>)}
          </div>
          {items.map(([name, col2, col3], i) => (
            <div className="ct-table__row" key={i}>
              <span className="ct-table__name">{name}</span>
              <span className="ct-table__val">{col2}</span>
              <span className="ct-table__val">{col3}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

/**
 * A table-shaped category created in the editor. Mirrors the Breads card, plus
 * the category controls — the built-in cards are fixed, these are not.
 */
const CustomTableSection = ({ data, isEditing = false, onUpdate, category }) => {
  const items = data.items || [];
  return (
    <div className="ct-card ct-card--open">
      <div className="ct-card__hd ct-card__hd--static">
        <span className="ct-card__icon">{data.icon || '🍽️'}</span>
        <span className="ct-card__title">{data.title}</span>
        <span className="ct-card__meta">{countLabel(items.length)}</span>
      </div>
      <div className="ct-card__body">
        {isEditing && (
          <CategoryTitleBar title={data.title} hint={countLabel(items.length)} {...category} />
        )}
        <ItemTable
          items={items}
          cols={data.cols || ['Item', 'Half', 'Full']}
          isEditing={isEditing}
          onUpdate={(next) => onUpdate?.({ ...data, items: next })}
        />
      </div>
    </div>
  );
};

const Catering = () => {
  const { role } = useAuth();
  const canEdit = role === 'admin' || role === 'owner';
  const { data, setData, loading, dirty, saving, publish, discard } = useCateringData();
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);

  useSeo({
    title: 'Catering',
    description: 'Indian catering in Northwest Arkansas — half & full trays of biryanis, curries, starters, breads & desserts for weddings, parties & corporate events. Request a quote.',
    path: '/Catering',
  });

  // Flat search index from all catering data
  const searchIndex = useMemo(() => {
    if (!data) return [];
    const results = [];
    const addTray = (section, label) => {
      section?.groups?.forEach((g) => {
        g.items?.forEach(([name, half, full]) => {
          if (name) results.push({ name, half, full, section: label });
        });
      });
    };
    const addTable = (items, label) => {
      items?.forEach(([name, col2, col3]) => {
        if (name) results.push({ name, half: col2, full: col3, section: label });
      });
    };
    addTray(data.starters, 'Starters');
    addTray(data.biryanis, 'Biryanis');
    addTray(data.curries, 'Curries');
    addTable(data.breads, 'Breads');
    addTable(data.drinksDesserts, 'Drinks & Desserts');
    addTable(data.breakfast, 'Breakfast');
    addTable(data.snacksTray, 'Snacks (Tray)');
    addTable(data.snacksPiece, 'Snacks (Per Piece)');
    (data.customSections || []).forEach((sec) => {
      if (sec.kind === 'table') addTable(sec.items, sec.title);
      else addTray(sec, sec.title);
    });
    return results;
  }, [data]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return searchIndex.filter((r) => r.name.toLowerCase().includes(q));
  }, [searchQuery, searchIndex]);

  // Clearing the query matters: the search box is hidden while editing, and a
  // leftover query would keep the categories behind a results list.
  const enterEdit = useCallback(() => { setSearchQuery(''); setIsEditing(true); }, []);

  const handleDiscard = useCallback(() => {
    discard();
    setIsEditing(false);
  }, [discard]);

  const handlePublish = useCallback(async () => {
    await publish(data);
    setIsEditing(false);
  }, [publish, data]);

  const upd = (key) => (val) => setData((prev) => ({ ...prev, [key]: val }));

  /* ── Categories added in the editor ── */
  const customSections = useMemo(() => data?.customSections || [], [data]);
  const setCustom = useCallback((updater) => {
    setData((prev) => ({ ...prev, customSections: updater(prev.customSections || []) }));
  }, [setData]);

  // Every card title on the page, so a new category can't shadow one — the
  // built-in cards included, even though they aren't editable themselves.
  const categoryNames = useMemo(() => [
    data?.starters?.title, data?.biryanis?.title, data?.curries?.title,
    'Breads', 'Drinks & Desserts', 'Breakfast', 'Snacks',
    ...customSections.map((s) => s.title),
  ].filter(Boolean), [data, customSections]);

  const addCustomSection = useCallback((spec) => {
    setCustom((list) => [...list, buildCateringSection(spec)]);
  }, [setCustom]);

  /* Every card in the first band, fixed and added alike, in one order. */
  const cards = useMemo(() => orderedCards(data), [data]);
  const handleMoveCard = useCallback((from, to) => {
    setData((prev) => moveCard(prev, from, to));
  }, [setData]);
  const handleUpdateCard = useCallback((entry, section) => {
    setData((prev) => updateCard(prev, entry, section));
  }, [setData]);

  const customCategory = (i) => ({
    takenNames: categoryNames,
    onRename: (title) => setCustom((list) => list.map((s, j) => (j === i ? { ...s, title } : s))),
    onDelete: () => setCustom((list) => list.filter((_, j) => j !== i)),
    onMoveUp: i > 0 ? () => setCustom((list) => {
      const next = [...list];
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    }) : undefined,
    onMoveDown: i < customSections.length - 1 ? () => setCustom((list) => {
      const next = [...list];
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return next;
    }) : undefined,
  });

  if (loading || !data) {
    return (
      <div className="ct" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#71717a' }}>Loading catering menu…</p>
      </div>
    );
  }

  return (
    <div className={`ct${isEditing ? ' ae-editing-body' : ''}`}>

      {canEdit && !isEditing && (
        <button className="ae-toggle" onClick={enterEdit} title="Enter edit mode">
          Edit Catering Menu
        </button>
      )}

      {isEditing && (
        <div className="ae-bar">
          <span className="ae-bar__info">
            <strong>Edit Mode</strong>{dirty ? ' — Unsaved changes' : ' — No changes yet'} · Click the pencil on any item
          </span>
          <div className="ae-bar__actions">
            <button className="ae-btn-discard" onClick={handleDiscard}>Discard</button>
            <button className="ae-btn-publish" onClick={handlePublish} disabled={saving || !dirty}>
              {saving ? 'Publishing…' : 'Publish Changes'}
            </button>
          </div>
        </div>
      )}

      <section className="ct-hero">
        <div className="ct-hero__overlay" />
        <div className="ct-hero__inner">
          <span className="ct-badge">Catering Services</span>
          <h1 className="ct-hero__title">Let Us Cater Your<br />Next Event</h1>
          <p className="ct-hero__quote">
            From small gatherings to grand celebrations, Desi Bites brings bold,
            authentic flavors to every table.
          </p>
          <a href="tel:+14794129916" className="ct-hero__btn">Call (479) 412-9916</a>
        </div>
      </section>

      <section className="ct-events">
        <div className="ct-events__inner">
          <Reveal>
            <h2 className="ct-events__heading">Perfect For Every Occasion</h2>
          </Reveal>
          <div className="ct-events__grid">
            {[
              { icon: '💍', title: 'Weddings & Sangeets', desc: 'Grand buffets for your special day' },
              { icon: '🏢', title: 'Corporate Events', desc: 'Team lunches & office celebrations' },
              { icon: '🎉', title: 'Parties & Gatherings', desc: 'Birthdays, graduations & more' },
              { icon: '🎊', title: 'All Events', desc: 'Any occasion, big or small — we cater it all' },
            ].map((e, i) => (
              <Reveal key={i}>
                <div className="ct-event">
                  <span className="ct-event__icon">{e.icon}</span>
                  <h3 className="ct-event__title">{e.title}</h3>
                  <p className="ct-event__desc">{e.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="ct-menu">
        <div className="ct-menu__inner">

          {/* ── Search bar ── */}
          {!isEditing && (
            <div className="ct-search">
              <div className="ct-search__inner">
                <svg className="ct-search__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/>
                </svg>
                <input
                  ref={searchRef}
                  type="search"
                  className="ct-search__input"
                  placeholder="Search catering items…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search catering items"
                />
                {searchQuery && (
                  <button className="ct-search__clear" onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }} aria-label="Clear">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Search results ── */}
          {searchQuery.trim() && (
            <div className="ct-search-results">
              {searchResults.length === 0 ? (
                <div className="ct-search-empty">No items found for <strong>"{searchQuery}"</strong></div>
              ) : (
                <>
                  <p className="ct-search-count">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for <strong>"{searchQuery}"</strong></p>
                  {searchResults.map((r, i) => (
                    <div key={i} className="ct-search-row">
                      <div className="ct-search-row__body">
                        <span className="ct-search-row__name">{r.name}</span>
                        <span className="ct-search-row__section">{r.section}</span>
                      </div>
                      <div className="ct-search-row__prices">
                        {r.half && r.half !== '—' && <span className="ct-search-row__price">${typeof r.half === 'number' ? r.half.toFixed(2) : String(r.half).replace(/^\$/, '')} <small>half</small></span>}
                        {r.full && r.full !== '—' && <span className="ct-search-row__price">${typeof r.full === 'number' ? r.full.toFixed(2) : String(r.full).replace(/^\$/, '')} <small>full</small></span>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {!searchQuery.trim() && (
            <>
              <p style={{ maxWidth: 720, margin: '0 auto 20px', textAlign: 'center', color: '#57534e', background: '#fff7ed', border: '1px solid #f1e4d3', borderRadius: 12, padding: '12px 16px', fontSize: 14.5, lineHeight: 1.5 }}>
                <strong>Tray guide:</strong> a <strong>Half Tray</strong> serves about <strong>8–10 people</strong> and a{' '}
                <strong>Full Tray</strong> about <strong>18–20</strong> as part of a spread. Portions vary by dish — ask us if you’re planning a full menu.
              </p>
              {/* ── Admin: reorder every card from one list ── */}
              {isEditing && (
                <CategoryOrderPanel
                  title="Catering category order"
                  items={cards.map((entry) => ({
                    id: entry.key,
                    icon: entry.section.icon || '🍽️',
                    label: entry.section.title,
                    hint: countLabel(entry.kind === 'table'
                      ? (entry.section.items || []).length
                      : (entry.section.groups || []).reduce((n, g) => n + g.items.length, 0)),
                  }))}
                  onMove={handleMoveCard}
                />
              )}

              {/* ── The cards themselves, in that order ── */}
              {cards.map((entry, pos) => {
                // Built-in cards move but can't be renamed or deleted — they
                // are part of the page, not rows in a list.
                const category = {
                  ...(entry.builtin ? {} : customCategory(entry.index)),
                  onMoveUp: pos > 0 ? () => handleMoveCard(pos, pos - 1) : undefined,
                  onMoveDown: pos < cards.length - 1 ? () => handleMoveCard(pos, pos + 1) : undefined,
                };
                const onUpdate = (next) => handleUpdateCard(entry, next);

                return entry.kind === 'table' ? (
                  <Reveal key={entry.key}>
                    <CustomTableSection
                      data={entry.section}
                      isEditing={isEditing}
                      onUpdate={onUpdate}
                      category={category}
                    />
                  </Reveal>
                ) : (
                  <TraySection
                    key={entry.key}
                    data={entry.section}
                    defaultOpen
                    isEditing={isEditing}
                    onUpdate={onUpdate}
                    category={category}
                  />
                );
              })}

              {isEditing && (
                <AddCategoryPanel
                  label="+ Add Catering Category"
                  heading="New catering category"
                  namePlaceholder="Category name — e.g. Live Counters"
                  options={CATERING_LAYOUTS}
                  takenNames={categoryNames}
                  onCreate={addCustomSection}
                />
              )}
            </>
          )}
        </div>
      </section>

      <section className="ct-feature">
        <div className="ct-feature__inner">
          <Reveal>
            <span className="ct-section-label">Refreshments</span>
            <h2 className="ct-feature__heading">Drinks &amp; Desserts</h2>
            <p className="ct-feature__sub">Gallon &amp; tray pricing for sweet endings</p>
          </Reveal>
          <Reveal>
            <div className="ct-feature__card">
              <ItemTable items={data.drinksDesserts} cols={['Item', 'Half', 'Full']} isEditing={isEditing} onUpdate={upd('drinksDesserts')} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="ct-menu ct-menu--alt">
        <div className="ct-menu__inner">
          <Reveal>
            <span className="ct-section-label">Morning &amp; Bites</span>
            <h2 className="ct-menu__heading">Breakfast &amp; Snacks</h2>
            <p className="ct-menu__sub">Per-piece &amp; tray pricing</p>
          </Reveal>
          <Reveal>
            <div className="ct-split">
              <div className="ct-split__col">
                <h3 className="ct-split__label" style={{ '--accent': '#d97706' }}>Breakfast</h3>
                <ItemTable items={data.breakfast} cols={['Item', 'Min.', 'Price']} isEditing={isEditing} onUpdate={upd('breakfast')} />
              </div>
              <div className="ct-split__divider" />
              <div className="ct-split__col">
                <h3 className="ct-split__label" style={{ '--accent': '#7c3aed' }}>Snacks</h3>
                <ItemTable items={data.snacksTray}  cols={['Item', 'HT', 'FT']}   isEditing={isEditing} onUpdate={upd('snacksTray')} />
                <div className="ct-split__gap" />
                <ItemTable items={data.snacksPiece} cols={['Item', 'Min.', 'Price']} isEditing={isEditing} onUpdate={upd('snacksPiece')} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="ct-menu">
        <div className="ct-menu__inner">
          <Reveal>
            <div className="ct-duo__card ct-duo__card--dark" style={{ maxWidth: 480, margin: '0 auto' }}>
              <h3 className="ct-duo__title">Service Add-Ons</h3>
              <div className="ct-addons">
                {data.addons.map((a, i) => (
                  <div className="ct-addon" key={i}>
                    <span className="ct-addon__label">{a.label}</span>
                    <span className="ct-addon__price">{a.price}</span>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 14, fontSize: 13, lineHeight: 1.55, color: '#d6d3d1' }}>
                <strong>Setup &amp; Sternos</strong> is a $100 flat fee for on-site setup with chafing
                dishes &amp; sternos that keep your food hot — reserved when you book. Plates &amp; cutlery
                are priced per guest, and delivery is quoted by distance.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <QuoteForm />

      <section className="ct-cta">
        <div className="ct-cta__inner">
          <Reveal>
            <h2 className="ct-cta__title">Prefer to Talk It Through?</h2>
            <p className="ct-cta__desc">
              Fill out the quote form above, or call us to discuss your event directly.
              We'd love to be part of your celebration!
            </p>
            <div className="ct-cta__actions">
              <a href="tel:+14794129916" className="ct-hero__btn">Call (479) 412-9916</a>
              <Link to="/menu" className="ct-cta__link">View Full Menu</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Catering;
